import * as A from 'fp-ts/Array'
import * as O from 'fp-ts/Option'
import { identity, pipe } from 'fp-ts/function'
import type { Subscription } from 'rxjs'
import { Subject } from 'rxjs'
import { bufferCount, take } from 'rxjs/operators'
import { getFieldsToReset, getValidetors, listToMapName } from '../FormSlave'
import type { FormWorker } from '../FormWorker'
import type { Validator } from './Validator'
import type { ValidatorMapResult } from './ValidatorResult'
import { ValidationResultType } from './ValidatorResult'
import type { ValidatorSubscriber } from './ValidatorSubscriber'

export type runType = 'init' | 'all' | 'changed'

export const mapFieldsToRunType = (
    fields: FormWorker.InternalValueFields,
    runType: runType
): FormWorker.ValueForRunValidators =>
    Object.values(fields).map((f): FormWorker.ValueForRunValidators[0] => ({ ...f, runType: runType }))

export const runAllValidators = (
    allValidatorsSubscription: O.Option<Subscription>,
    validatorSubscriber: ValidatorSubscriber,
    fieldsToValidate: FormWorker.ValueForRunValidators,
    fields: FormWorker.InternalValueFields,
    unitsForm: FormWorker.Props['unitsForm'],
    dispatchValidatorsResult: FormWorker.dispatchValidatorsResult,
    resetOnChange: FormWorker.Props['resetOnChange'],
    formValidators: FormWorker.Props['validators'],
    onComplete?: (rr: FormWorker.InternalValidatorFields) => void
): O.Option<Subscription> => {
    unscribeSubscription(allValidatorsSubscription)

    const yepFields = fieldsToValidate
        .filter(f => f.use === 'yep')
        .filter(f =>
            pipe(
                O.fromNullable(f.initialValue),
                O.match(
                    () => f.runType === 'all' || f.runType === 'changed',
                    () => true
                )
            )
        )

    const yepFieldsNames = yepFields.map(f => f.name)

    type decorateValueForRunValidators = Array<FormWorker.InternalValueField & { runType: runType | 'multiple' }>

    const yepFieldsToEvaluate = pipe(
        O.fromNullable(formValidators),
        O.map(vs =>
            Object.keys(vs)
                .filter(vName => !yepFieldsNames.includes(vs[vName].showErrorOn.toString()))
                .filter(vName => yepFieldsNames.filter(f => vs[vName].fieldsObserved.includes(f)).length > 0)
                .map(vName => vs[vName].showErrorOn.toString())
                .filter(showErrorOnName => fields[showErrorOnName].use === 'yep')
        ),
        O.getOrElse<(keyof typeof fields)[]>(() => []),
        A.reduce(
            yepFields,
            (ys: decorateValueForRunValidators, fName): decorateValueForRunValidators => [
                ...ys,
                { ...fields[fName], runType: 'multiple' },
            ]
        )
    )

    if (yepFieldsToEvaluate.length === 0) {
        onComplete && onComplete({})

        return O.none
    }

    const validatorsProcess = new Subject<FormWorker.InternalValidatorField>()
    const nAllValidatorsSubscription = validatorsProcess
        .pipe(bufferCount(yepFieldsToEvaluate.length), take(1))
        .subscribe(r => {
            onComplete && onComplete(r.reduce(listToMapName, {}))
        })

    yepFieldsToEvaluate.forEach(f =>
        runFieldValidators(
            validatorSubscriber,
            f,
            fields,
            getValidetors(f.name, unitsForm, formValidators, f.runType === 'multiple'),
            dispatchValidatorsResult,
            resetOnChange,
            f.runType === 'init' ? 'all' : f.runType,
            r => validatorsProcess.next(r)
        )
    )

    return O.some(nAllValidatorsSubscription)
}

export const runFieldValidators = (
    validatorSubscriber: ValidatorSubscriber,
    field: FormWorker.InternalValueField,
    fields: FormWorker.InternalValueFields,
    validators: Validator.validatorsBuilded | undefined,
    dispatchValidatorsResult: FormWorker.dispatchValidatorsResult,
    resetOnChange: FormWorker.Props['resetOnChange'],
    validationStep: 'multiple' | 'changed' | 'all',
    onComplete: (r: FormWorker.InternalValidatorField) => void
) => {
    dispatchValidatorsResult({
        type: 'starting',
        payload: {
            name: field.name,
            fieldsToReset: validationStep === 'changed' ? getFieldsToReset(resetOnChange, field.name) : [],
        },
    })

    pipe(
        O.fromNullable(validators),
        O.filter(_ => field.use === 'yep'),
        O.map(vs => {
            validatorSubscriber.subscribeValidator(
                field.name,
                field.value,
                field.data,
                fields,
                vs,
                (mapResult: ValidatorMapResult) => {
                    const isValid =
                        Object.values(mapResult)
                            .map(mr => mr.type === ValidationResultType.ERROR || mr.type === ValidationResultType.FAIL)
                            .filter(identity).length === 0

                    const canUnknow =
                        validationStep !== 'all' &&
                        Object.values(mapResult)
                            .map(mr => mr.type === ValidationResultType.UNKNOW)
                            .filter(identity).length > 0

                    const r: FormWorker.InternalValidatorField = canUnknow
                        ? {
                              name: field.name,
                              isValidating: false,
                              isValid: 'unknown',
                          }
                        : {
                              name: field.name,
                              isValidating: false,
                              isValid: isValid ? 'valid' : 'notValid',
                              validatorResults: mapResult,
                          }

                    dispatchValidatorsResult({
                        type: 'completed',
                        payload: r,
                    })
                    onComplete(r)
                }
            )
        }),
        O.getOrElse(() => {
            const r: FormWorker.InternalValidatorField = {
                name: field.name,
                validatorResults: {},
                isValid: 'valid',
                isValidating: false,
            }
            dispatchValidatorsResult({ type: 'completed', payload: r })
            onComplete(r)
        })
    )
}

export const unscribeSubscription = (optSubscription: O.Option<Subscription>) =>
    pipe(
        optSubscription,
        O.filter(s => s.closed),
        O.map(s => {
            s.unsubscribe()

            return s
        })
    )
